import { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { WD } from '../../../warningDashboard/warningTheme';
import { ENTRIES_URL, FIGURE, LEVEL_TONE } from '../utils/constants';
import { fmtHours, minutesBetween } from '../utils/hours';
import useSave from '../utils/useSave';

/**
 * Log time against a plan - a day (not in the future), from -> to, and an optional note. With `entry` it edits that entry
 * instead. The server refuses overlaps and tasks on hold, and says why.
 */
export default function EntryForm({ plan, today, onChanged, entry = null, onDone }) {
  const [date, setDate] = useState(entry?.workDate || today);
  const [fromTime, setFrom] = useState(entry?.fromTime || '');
  const [toTime, setTo] = useState(entry?.toTime || '');
  const [note, setNote] = useState(entry?.note || '');
  const [busy, run] = useSave(onChanged);

  const minutes = fromTime && toTime ? minutesBetween(fromTime, toTime) : 0;
  const valid = date && fromTime && toTime && minutes > 0 && date <= today;

  const submit = (event) => {
    event.preventDefault();
    if (!valid) return;
    const payload = { workDate: date, fromTime, toTime, note: note.trim() || null };
    const done = () => {
      if (!entry) {
        setFrom('');
        setTo('');
        setNote('');
      }
      if (onDone) onDone();
    };
    run(
      entry
        ? { method: 'patch', url: `${ENTRIES_URL}/${entry.entryId}`, data: payload }
        : { method: 'post', url: ENTRIES_URL, data: { hourId: plan.hourId, ...payload } },
      done,
    );
  };

  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 1 }}>
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
        <TextField type="date" size="small" required value={date} onChange={(event) => setDate(event.target.value)} inputProps={{ max: today, 'aria-label': 'Work date' }} />
        <TextField type="time" size="small" required value={fromTime} onChange={(event) => setFrom(event.target.value)} inputProps={{ 'aria-label': 'From time' }} />
        <Typography aria-hidden sx={{ color: WD.inkFaint }}>→</Typography>
        <TextField type="time" size="small" required value={toTime} onChange={(event) => setTo(event.target.value)} inputProps={{ 'aria-label': 'To time' }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, ...FIGURE, color: minutes > 0 ? WD.ink : LEVEL_TONE.overloaded.text }}>
          {minutes > 0 ? fmtHours(minutes / 60) : fromTime && toTime ? 'to must be after from' : ''}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          size="small"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Note (optional)"
          inputProps={{ maxLength: 400, 'aria-label': 'Note' }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button type="submit" size="small" variant="contained" disabled={busy || !valid} startIcon={entry ? null : <AddRoundedIcon />}>
          {entry ? 'Save' : 'Log time'}
        </Button>
        {entry && (
          <Button size="small" onClick={onDone}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}
